import BasicButtonGroup from 'components/BasicButtonGroup';
import BasicCheckbox from 'components/BasicCheckbox';
import BasicInput from 'components/BasicInput';
import BasicLabel from 'components/BasicLabel';
import BasicSubmitButton from 'components/BasicSubmitButton';
import DatePicker from 'components/DatePicker';
import FormContentLoader from 'components/FormContentLoader';
import LazyLoadSelect from 'components/LazyLoadSelect';
import LinkButton from 'components/LinkButton';
import ProcessStartDateSelect from 'components/ProcessStartDateSelect';
import TextEditor from 'components/TextEditor';
import {ANIMATION_TIMEOUT_APPEAR} from 'constants/animation.js';
import {ENDPOINT} from 'constants/endpoints';
import {ErrorMessage, Formik} from 'formik';
import {convertHtmlToEditorState} from 'helpers/formik';

import './styles.css';
import {useApi} from 'hooks/useApi';
import {useFiltersAccordion} from 'hooks/useFiltersAccordion';
import PropTypes from 'prop-types';
import React, {useEffect, useMemo} from 'react';
import {CSSTransition} from 'react-transition-group';
import FollowUpEmailTemplateViewDialog from 'scenes/Dialog/components/FollowUpEmailTemplateViewDialog';

import FiltersAccordion from 'scenes/FiltersAccordion';
import FiltersPartial from 'scenes/FiltersAccordion/components/FiltersPartial';

import {ProcessFormValidationSchema} from 'scenes/Form/components/ProcessCreateUpdateForm/validate';
import LinkBuilder from '../../../../components/LinkBuilder';

const ProcessCreateUpdateForm = props => {
  const {
    title,
    values,
    filters,
    isFetched,
    isSubscriptionProcess,
    hasFieldHidden = false,
    onSaveFormHandler,
    onCloseFormHandler,
  } = props;

  const api = useApi();

  const [
    { sections, query },
    {
      init: initFilters,
      clear,
      change,
      groupClear,
      groupSelect,
      push,
      search: filterSearch,
    },
  ] = useFiltersAccordion();

  useEffect(() => {
    if (isFetched) {
      initFilters(filters);
    }
  }, [isFetched]);

  const onSaveForm = (values, form) => {
    const filters = query;
    Object.keys(query).forEach(key => {
      if (filters[key] && !filters[key].length) {
        delete filters[key];
      }
    });
    onSaveFormHandler(values, filters, form);
  };

  const initValues = useMemo(() => {
    const startDate = values.StartDateYear
      ? {
          month: Number(values.StartDateMonth),
          year: Number(values.StartDateYear),
        }
      : {};

    const publishedOn = values.DateAdd
      ? new Date(values.DateAdd.split(' ')[0])
      : new Date();

    return {
      ...values,
      AboutUs: convertHtmlToEditorState(values.AboutUs),
      Descriptions: convertHtmlToEditorState(values.Descriptions),
      AdditionalDetails: convertHtmlToEditorState(values.AdditionalDetails),
      Hidden: values.Hidden === 'Yes',
      AppDeadline: values.AppDeadline === 'Yes',
      AppDeadlineDate: values.AppDeadlineDate
        ? new Date(values.AppDeadlineDate)
        : null,
      gatreq: values.gatreq === 'Yes',
      recomreq: values.recomreq === 'Yes',
      CoverLetterRequire: values.CoverLetterRequire === 'Yes',
      BlockLinkedInSharing: values.BlockLinkedInSharing === 'Yes',
      startDate,
      preDefinedStartDate: startDate || null,
      publishedOn,
      EmailTemplateFollowupID: values.EmailTemplateFollowupID || null,
      hasFollowUpEmail: !!values.EmailTemplateFollowupID,
      hasATSLink: !!values.ATSLink,
    };
  }, [values]);

  const handleChangeHidden = (e, setFieldValue) => {
    const { checked, name } = e.target;
    setFieldValue(name, checked);
    if (checked) {
      setFieldValue('BlockLinkedInSharing', true, false);
    }
  };

  const getEmailTemplateFollowupOptions = (query, offset) => {
    let endpoint = ENDPOINT.FORM.OPTIONS.MANAGE_JOBS_EMAIL_TEMPLATE_FOLLOWUP;
    let params = { query, offset };
    if (isSubscriptionProcess) {
      endpoint =
        ENDPOINT.FORM.OPTIONS.SUBSCRIPTION_SEARCHES_EMAIL_TEMPLATE_FOLLOWUP;
      params.id = values.ProcessID;
    }
    return new Promise(resolve =>
      api.get(endpoint, params, {
        handleComplete: ({ data }) => resolve(data),
      })
    );
  };

  const handleViewEmailTemplateFollowupID = id => {
    FollowUpEmailTemplateViewDialog({ id, type: 'job' });
  };

  return (
    <CSSTransition
      in={true}
      appear={true}
      timeout={ANIMATION_TIMEOUT_APPEAR}
      classNames="fade"
    >
      <div className="form__inner">
        <div className="form__header">
          <h4 className="form__title">{title}</h4>
        </div>

        <Formik
          initialValues={initValues}
          validationSchema={ProcessFormValidationSchema}
          onSubmit={onSaveForm}
          enableReinitialize={true}
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            isSubmitting,
            setFieldValue,
            setFieldTouched,
          }) => (
            <form onSubmit={handleSubmit} className="form__content">
              <div className="form__group">
                {(isSubmitting || !isFetched) && <FormContentLoader />}
                <BasicInput
                  label="Position Title"
                  required={true}
                  name="PositionTitle"
                  inputType="text"
                  content={values.PositionTitle}
                  placeholder="0 characters of the 150 character limit"
                  controlFunc={handleChange}
                  isValid={
                    (!errors.PositionTitle && touched.PositionTitle) ||
                    !touched.PositionTitle
                  }
                  touchFunc={handleBlur}
                  labelStyles={{ fontWeight: 'bold' }}
                />

                <ErrorMessage
                  name="PositionTitle"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              <div className="form__group">
                <BasicLabel
                  name="AboutUs"
                  required={true}
                  text="About Us"
                  styles={{
                    fontWeight: 'bold',
                    marginTop: '15px',
                  }}
                />

                <TextEditor
                  name="AboutUs"
                  enableHyperlinks
                  editorState={values.AboutUs}
                  controlFunc={setFieldValue}
                  touchFunc={setFieldTouched}
                  isValid={
                    (!errors.AboutUs && !!touched.AboutUs) || !touched.AboutUs
                  }
                />

                <ErrorMessage
                  name="AboutUs"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              <div className="form__group">
                <BasicLabel
                  name="Descriptions"
                  required={true}
                  text="Description"
                  styles={{
                    fontWeight: 'bold',
                    marginTop: '15px',
                  }}
                />
                <TextEditor
                  name="Descriptions"
                  enableHyperlinks
                  editorState={values.Descriptions}
                  controlFunc={setFieldValue}
                  touchFunc={setFieldTouched}
                  isValid={
                    (!errors.Descriptions && !!touched.Descriptions) ||
                    !touched.Descriptions
                  }
                />

                <ErrorMessage
                  name="Descriptions"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>
              <div className="form__group" style={{ marginBottom: '30px' }}>
                <ProcessStartDateSelect
                  required
                  label="Start Date"
                  name="startDate"
                  controlFunc={setFieldValue}
                  touchFunc={setFieldTouched}
                  values={values.startDate}
                  preDefinedValues={values.preDefinedStartDate}
                  isValid={
                    !(errors.startDate && !touched.startDate) ||
                    !touched.startDate
                  }
                />

                <ErrorMessage
                  name="startDate"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <FiltersAccordion
                title="Application Requirements"
                subTitle="Use the search filters below to narrow your confidential search."
                filters={sections}
                isFetching={false}
                onChange={change}
                onGroupSelectAll={groupSelect}
                onGroupClearAll={groupClear}
                onClearAll={clear}
                onSearch={filterSearch}
                onPushValue={push}
                render={renderProps => <FiltersPartial {...renderProps} />}
              />
              <div className="form__group">
                <BasicLabel
                  name="additionalOptions"
                  required={false}
                  text="Additional Options"
                  styles={{
                    fontWeight: 'bold',
                    marginTop: '15px',
                  }}
                />
                <div className="form__row">
                  <BasicCheckbox
                    value="Yes"
                    singleRow
                    checked={values.hasFollowUpEmail}
                    title="Post Application Follow-Up Email"
                    name="hasFollowUpEmail"
                    controlFunc={handleChange}
                    labelStyles={{
                      fontFamily: 'Noto Sans, sans-serif',
                    }}
                  />
                </div>
                {values.hasFollowUpEmail && (
                  <>
                    <div
                      className="form__row form__row--fixed"
                      style={{ marginBottom: '15px' }}
                    >
                      <div style={{ width: '80%' }}>
                        <LazyLoadSelect
                          isSearchable={true}
                          name="EmailTemplateFollowupID"
                          value={values.EmailTemplateFollowupID}
                          isValid={
                            (!errors.EmailTemplateFollowupID &&
                              !!touched.EmailTemplateFollowupID) ||
                            !touched.EmailTemplateFollowupID
                          }
                          required={false}
                          isMulti={false}
                          placeholder="Select a template"
                          getOption={getEmailTemplateFollowupOptions}
                          onChange={setFieldValue}
                          onBlur={handleBlur}
                        />
                      </div>
                      {values.EmailTemplateFollowupID && (
                        <div style={{ marginLeft: '10px' }}>
                          <LinkButton
                            text="View"
                            controlFunc={() =>
                              handleViewEmailTemplateFollowupID(
                                values.EmailTemplateFollowupID.value
                              )
                            }
                          />
                        </div>
                      )}
                    </div>
                    <ErrorMessage
                      name="EmailTemplateFollowupID"
                      render={message => (
                        <div
                          className="form__error"
                          style={{ marginTop: '-16px' }}
                        >
                          {message}
                        </div>
                      )}
                    />
                  </>
                )}
                <div className="form__row">
                  <BasicCheckbox
                    value="Yes"
                    singleRow
                    checked={values.AppDeadline}
                    title="Application Deadline"
                    name="AppDeadline"
                    controlFunc={handleChange}
                    labelStyles={{
                      fontFamily: 'Noto Sans, sans-serif',
                    }}
                  />

                  {values.AppDeadline && (
                    <DatePicker
                      name="AppDeadlineDate"
                      onChange={setFieldValue}
                      dateFormat="MM/dd/yyyy"
                      selected={values.AppDeadlineDate}
                      minDate={values.publishedOn}
                      icon={true}
                    />
                  )}
                </div>
                <ErrorMessage
                  name="AppDeadlineDate"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
                <div className="form__row">
                  <BasicCheckbox
                    value={values.recomreq}
                    singleRow
                    checked={values.recomreq}
                    name="recomreq"
                    title="Require Recommendation"
                    controlFunc={handleChange}
                    labelStyles={{
                      fontFamily: 'Noto Sans, sans-serif',
                    }}
                  />
                </div>

                <div className="form__row">
                  <BasicCheckbox
                    value={values.gatreq}
                    singleRow
                    checked={values.gatreq}
                    title="Require GoBuyside Aptitude Test"
                    name="gatreq"
                    controlFunc={handleChange}
                    labelStyles={{
                      fontFamily: 'Noto Sans, sans-serif',
                    }}
                  />
                </div>

                <div className="form__row">
                  <BasicCheckbox
                    value={values.CoverLetterRequire}
                    singleRow
                    checked={values.CoverLetterRequire}
                    title="Require Cover Letter"
                    name="CoverLetterRequire"
                    controlFunc={handleChange}
                    labelStyles={{
                      fontFamily: 'Noto Sans, sans-serif',
                    }}
                  />
                </div>

                {hasFieldHidden && (
                  <div className="form__row">
                    <BasicCheckbox
                      value={values.Hidden}
                      singleRow
                      checked={values.Hidden}
                      title="Remove from Job Board"
                      name="Hidden"
                      controlFunc={e => handleChangeHidden(e, setFieldValue)}
                      labelStyles={{
                        fontFamily: 'Noto Sans, sans-serif',
                      }}
                    />
                  </div>
                )}

                <div className="form__row">
                  <BasicCheckbox
                    disabled={values.Hidden}
                    value={values.BlockLinkedInSharing}
                    singleRow
                    checked={values.BlockLinkedInSharing}
                    name="BlockLinkedInSharing"
                    title="Block Linked-In Sharing"
                    controlFunc={handleChange}
                    labelStyles={{
                      fontFamily: 'Noto Sans, sans-serif',
                    }}
                  />
                </div>

                <div className="form__row">
                  <BasicCheckbox
                    value={values.hasATSLink}
                    singleRow
                    checked={values.hasATSLink}
                    name="hasATSLink"
                    title="Add ATS Link (Greenhouse, Work day or BambooHR)"
                    controlFunc={handleChange}
                    labelStyles={{
                      fontFamily: 'Noto Sans, sans-serif',
                    }}
                  />
                </div>

                {values.hasATSLink && (
                  <LinkBuilder
                    name="ATSLink"
                    value={values.ATSLink || ''}
                    placeholder="0 characters of the 512 character limit"
                    isValid={(!errors.ATSLink && touched.ATSLink) || !touched.ATSLink}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    setFieldValue={setFieldValue}
                  />
                )}
              </div>

              <div className="form__group" style={{ marginBottom: '30px' }}>
                <BasicLabel
                  name="additional"
                  required={false}
                  text="Additional Details"
                  styles={{
                    fontWeight: 'bold',
                    marginTop: '15px',
                  }}
                />

                <TextEditor
                  name="AdditionalDetails"
                  enableHyperlinks
                  editorState={values.AdditionalDetails}
                  controlFunc={setFieldValue}
                  touchFunc={setFieldTouched}
                  isValid={
                    (!errors.AdditionalDetails &&
                      !!touched.AdditionalDetails) ||
                    !touched.AdditionalDetails
                  }
                />

                <ErrorMessage
                  name="AdditionalDetails"
                  render={message => (
                    <div className="form__error">{message}</div>
                  )}
                />
              </div>

              <div className="form__controls">
                <BasicButtonGroup>
                  <BasicSubmitButton
                    disabled={isSubmitting}
                    controlFunc={handleSubmit}
                    btnWidth={200}
                    btnHeight={40}
                    btnFontWeight={500}
                    btnFontSize="0.8571rem"
                    btnArrow={true}
                    btnTextColor="#27282d"
                    btnText="Save"
                  />

                  <button
                    type="button"
                    className="form__close-btn"
                    onClick={onCloseFormHandler}
                  >
                    Close form
                  </button>
                </BasicButtonGroup>
              </div>
            </form>
          )}
        </Formik>
      </div>
    </CSSTransition>
  );
};

ProcessCreateUpdateForm.propTypes = {
  title: PropTypes.string,
  values: PropTypes.object,
  filters: PropTypes.object.isRequired,
  isFetched: PropTypes.bool.isRequired,
  isSubscriptionProcess: PropTypes.bool.isRequired,
  hasFieldHidden: PropTypes.bool,
  onSaveFormHandler: PropTypes.func.isRequired,
  onCloseFormHandler: PropTypes.func.isRequired,
};

export default ProcessCreateUpdateForm;
